import type { Edge, Node } from "@xyflow/react";
import dagre from "dagre";

interface LayoutOptions {
  rankdir?: "TB" | "LR" | "BT" | "RL";
  nodesep?: number;
  ranksep?: number;
  ranker?: "network-simplex" | "tight-tree" | "longest-path";
  defaultWidth?: number;
  defaultHeight?: number;
}

const DEFAULT_OPTIONS: Required<LayoutOptions> = {
  rankdir: "LR",
  nodesep: 40,
  ranksep: 120,
  ranker: "tight-tree",
  defaultWidth: 220,
  defaultHeight: 90,
};

export function layoutGraph(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {},
): { nodes: Node[]; edges: Edge[] } {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: opts.rankdir,
    nodesep: opts.nodesep,
    ranksep: opts.ranksep,
    ranker: opts.ranker,
  });
  g.setDefaultEdgeLabel(() => ({}));

  for (const node of nodes) {
    const width =
      typeof node.width === "number"
        ? node.width
        : ((node.style?.width as number) ?? opts.defaultWidth);
    const height =
      typeof node.height === "number"
        ? node.height
        : ((node.style?.height as number) ?? opts.defaultHeight);
    g.setNode(node.id, { width, height });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  const laidOut = nodes.map((node) => {
    const pos = g.node(node.id);
    if (!pos) return node;
    const width =
      typeof node.width === "number"
        ? node.width
        : ((node.style?.width as number) ?? opts.defaultWidth);
    const height =
      typeof node.height === "number"
        ? node.height
        : ((node.style?.height as number) ?? opts.defaultHeight);
    return {
      ...node,
      position: { x: pos.x - width / 2, y: pos.y - height / 2 },
    };
  });

  return { nodes: laidOut, edges };
}
